import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Turns a list of live DOM nodes into a Base64-encoded A4 PDF.
 *
 * ⚡ Compression:
 *  - JPEG (quality 0.72) instead of PNG (10-20× smaller, still readable at print quality)
 *  - html2canvas scale 1.5 (was 2×) — still crisper than 96 dpi, saves ~1.8× per page
 *  - jsPDF `compress: true` uses FlateDecode on the object stream
 *  - White background stripped so JPEG doesn't waste bytes
 */
export async function nodesToBase64Pdf(nodeIds) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'p', compress: true });
  let first = true;
  for (const nid of nodeIds) {
    const node = document.getElementById(nid);
    if (!node) continue;
    const canvas = await html2canvas(node, { scale: 1.5, useCORS: true, backgroundColor: '#ffffff', logging: false });
    // JPEG at 0.72 quality — near-identical visual fidelity to eye, ~10× smaller than PNG.
    const imgData = canvas.toDataURL('image/jpeg', 0.72);
    const pdfW = pdf.internal.pageSize.getWidth();
    const pdfH = pdf.internal.pageSize.getHeight();
    const imgH = (canvas.height * pdfW) / canvas.width;
    if (!first) pdf.addPage();
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, Math.min(imgH, pdfH), undefined, 'FAST');
    first = false;
  }
  const dataUri = pdf.output('datauristring');
  return dataUri.split('base64,')[1];
}

export const DOC_CATALOGUE = [
  { key: 'welcome', label: 'Welcome Letter', nodeIds: ['pw-welcome'], filename: 'Welcome_Letter.pdf' },
  { key: 'certificate', label: 'Policy Certificate', nodeIds: ['pw-certificate'], filename: 'Policy_Certificate.pdf' },
  { key: 'invoice', label: 'Invoice', nodeIds: ['pw-invoice'], filename: 'Invoice.pdf' },
  { key: 'terms', label: 'Terms & Conditions', nodeIds: ['pw-terms-1', 'pw-terms-2', 'pw-terms-3', 'pw-terms-4'], filename: 'Terms_and_Conditions.pdf' },
  { key: 'renewal', label: 'Renewal Letter', nodeIds: ['pw-renewal'], filename: 'Renewal_Letter.pdf' },
  { key: 'cancellation-letter', label: 'Cancellation Letter', nodeIds: ['pw-cancellation-letter'], filename: 'Cancellation_Letter.pdf', cancelledOnly: true },
  { key: 'cancellation-invoice', label: 'Cancellation Invoice', nodeIds: ['pw-cancellation-invoice'], filename: 'Cancellation_Invoice.pdf', cancelledOnly: true },
];

export const DEFAULT_ATTACHMENTS_FOR_TEMPLATE = {
  welcome: ['welcome', 'certificate', 'invoice'],
  renewal: ['renewal', 'certificate'],
  cancellation: ['cancellation-letter', 'cancellation-invoice'],
  'cancellation-reminder': ['cancellation-invoice'],
  service_scheduled: [],
  service_completed: [],
  payment_reminder: ['invoice'],
  reminder: ['invoice'],
  followup: [],
  quote: ['invoice'],
  receipt: ['certificate', 'invoice'],
};

export const availableDocs = (isCancelled) => DOC_CATALOGUE.filter((d) => !d.cancelledOnly || isCancelled);

/** Approximate decoded byte size of a base64 string. */
export const base64ByteSize = (b64) => Math.floor((b64.length * 3) / 4) - (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);

/** Format bytes as a friendly label. */
export const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

// Resend's outbound cap on a single email is 40 MB. We keep a safety margin so the base64
// wire-encoding overhead (~33%) never puts us over the wall.
export const MAX_TOTAL_ATTACH_BYTES = 20 * 1024 * 1024; // 20 MB decoded
export const MAX_PER_ATTACH_BYTES = 10 * 1024 * 1024; // 10 MB decoded per single file

/**
 * Build the attachment payload from a set of selected doc keys — with compression, size
 * validation, and clear error messages. Never returns silently — either every requested
 * attachment succeeds AND fits under the size cap, or it throws.
 */
export async function buildAttachments(selectedKeys, planNumber, isCancelled) {
  const chosen = availableDocs(isCancelled).filter((d) => selectedKeys.includes(d.key));
  const failed = [];
  const result = [];
  let total = 0;

  for (const doc of chosen) {
    let b64;
    try {
      b64 = await nodesToBase64Pdf(doc.nodeIds);
    } catch (e) {
      failed.push({ label: doc.label, reason: e?.message || 'render error' });
      continue;
    }
    if (!b64) {
      failed.push({ label: doc.label, reason: 'document is not visible on this page — cannot render' });
      continue;
    }
    const size = base64ByteSize(b64);
    if (size > MAX_PER_ATTACH_BYTES) {
      failed.push({ label: doc.label, reason: `${fmtSize(size)} exceeds the ${fmtSize(MAX_PER_ATTACH_BYTES)} per-file cap` });
      continue;
    }
    total += size;
    result.push({
      filename: `${planNumber || 'DHS'}_${doc.filename}`,
      content: b64,
      content_type: 'application/pdf',
      size,
    });
  }

  if (failed.length > 0) {
    const msg = failed.map((f) => `• ${f.label}: ${f.reason}`).join('\n');
    const err = new Error(`One or more attachments could not be processed:\n${msg}`);
    err.failed = failed;
    throw err;
  }
  if (total > MAX_TOTAL_ATTACH_BYTES) {
    throw new Error(`Combined attachment size ${fmtSize(total)} exceeds the ${fmtSize(MAX_TOTAL_ATTACH_BYTES)} limit. Please send fewer documents in one email.`);
  }

  return { attachments: result, chosen, totalBytes: total };
}
